import React, { useCallback, useEffect, useRef, useState } from 'react'

import { SelectionToolbar } from './toolbar/SelectionToolbar'
import { type ToolbarPosition } from './common/types'

import './RichTextEditor.scss'

interface ToolbarState {
  position: ToolbarPosition
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  isLink: boolean
}

interface VideoPreviewDescriptionEditorProps {
  value: string
  onBlur: (html: string) => void
  placeholder?: string
}

const RichTextEditor = ({
  value,
  onBlur,
  placeholder,
}: VideoPreviewDescriptionEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const savedRangeRef = useRef<Range | null>(null)
  const [toolbarState, setToolbarState] = useState<ToolbarState | null>(null)
  const [showLinkPopover, setShowLinkPopover] = useState(false)
  const [linkPanelInitialUrl, setLinkPanelInitialUrl] = useState('')
  const [isEditorEmpty, setIsEditorEmpty] = useState(true)

  const getNormalizedEditorHtml = useCallback(
    (editor: HTMLDivElement | null) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!editor || editor.textContent === null) {
        return ''
      }

      const normalizedText = editor.textContent.replace(/\u00A0/g, ' ').trim()
      const normalizedHtml = editor.innerHTML
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .trim()

      return normalizedText.length === 0 && normalizedHtml.length === 0
        ? ''
        : editor.innerHTML
    },
    []
  )

  // contenteditable adds a <br> when empty, so we need to account for that when determining if the editor is empty
  const updateEmptyState = useCallback(
    (editor: HTMLDivElement | null) => {
      setIsEditorEmpty(getNormalizedEditorHtml(editor).length === 0)
    },
    [getNormalizedEditorHtml]
  )

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value
      updateEmptyState(editorRef.current)
    }
  }, [updateEmptyState, value])

  const getSelectedAnchorElement = useCallback((selection: Selection) => {
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
    const candidateNodes = [
      selection.anchorNode,
      selection.focusNode,
      range?.commonAncestorContainer,
    ]

    for (const node of candidateNodes) {
      const element =
        node instanceof Element ? node : (node?.parentElement ?? null)

      const anchor = element?.closest('a')
      if (anchor) {
        return anchor
      }
    }

    return null
  }, [])

  const refreshToolbarFromSelection = useCallback(() => {
    const selection = window.getSelection()

    if (
      !selection ||
      selection.isCollapsed ||
      !editorRef.current?.contains(selection.anchorNode)
    ) {
      setToolbarState(null)
      return
    }

    const range = selection.getRangeAt(0)
    const selectionRect = range.getBoundingClientRect()
    const containerRect = containerRef.current?.getBoundingClientRect()

    if (!containerRect) return

    const anchorEl = getSelectedAnchorElement(selection)
    setToolbarState({
      position: {
        top: selectionRect.top - containerRect.top,
        left: selectionRect.left - containerRect.left + selectionRect.width / 2,
      },
      isBold: document.queryCommandState('bold'),
      isItalic: document.queryCommandState('italic'),
      isUnderline: document.queryCommandState('underline'),
      isLink: !!anchorEl,
    })
  }, [getSelectedAnchorElement])

  const updateToolbarState = useCallback(() => {
    if (showLinkPopover) {
      const focusInToolbar = containerRef.current
        ?.querySelector('.description-toolbar')
        ?.contains(document.activeElement)

      if (focusInToolbar) return

      setShowLinkPopover(false)
      setLinkPanelInitialUrl('')
    }

    refreshToolbarFromSelection()
  }, [refreshToolbarFromSelection, showLinkPopover])

  useEffect(() => {
    document.addEventListener('selectionchange', updateToolbarState)
    return () =>
      document.removeEventListener('selectionchange', updateToolbarState)
  }, [updateToolbarState])

  const handleFormat = useCallback((command: string) => {
    document.execCommand(command, false)
    editorRef.current?.focus()
  }, [])

  const focusEditor = useCallback(() => {
    queueMicrotask(() => {
      editorRef.current?.focus()
    })
  }, [])

  const closeLinkPopover = useCallback((clearInitialUrl = true) => {
    setShowLinkPopover(false)
    if (clearInitialUrl) {
      setLinkPanelInitialUrl('')
    }
  }, [])

  const handleLinkClick = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      savedRangeRef.current = selection.getRangeAt(0).cloneRange()
    }
    const anchor = selection ? getSelectedAnchorElement(selection) : null
    setLinkPanelInitialUrl(anchor?.getAttribute('href') ?? '')
    setShowLinkPopover(true)
  }, [getSelectedAnchorElement])

  const restoreSavedRange = useCallback(() => {
    if (!savedRangeRef.current) return
    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(savedRangeRef.current)
    }
  }, [])

  const syncToolbarAfterCommand = useCallback(() => {
    requestAnimationFrame(() => {
      refreshToolbarFromSelection()
    })
  }, [refreshToolbarFromSelection])

  const clearSelectionState = useCallback(() => {
    savedRangeRef.current = null
    window.getSelection()?.removeAllRanges()
    setToolbarState(null)
    closeLinkPopover()
  }, [closeLinkPopover])

  const handleLinkConfirm = useCallback(
    (url: string) => {
      closeLinkPopover()
      restoreSavedRange()
      document.execCommand('createLink', false, url)
      savedRangeRef.current = null
      setToolbarState((prev) => (prev ? { ...prev, isLink: true } : prev))
      focusEditor()
      syncToolbarAfterCommand()
    },
    [closeLinkPopover, focusEditor, restoreSavedRange, syncToolbarAfterCommand]
  )

  const handleUnlink = useCallback(() => {
    closeLinkPopover()
    restoreSavedRange()
    document.execCommand('unlink', false)
    savedRangeRef.current = null
    setToolbarState((prev) => (prev ? { ...prev, isLink: false } : prev))
    focusEditor()
    syncToolbarAfterCommand()
  }, [
    closeLinkPopover,
    focusEditor,
    restoreSavedRange,
    syncToolbarAfterCommand,
  ])

  const handleLinkCancel = useCallback(() => {
    closeLinkPopover()
    restoreSavedRange()
    savedRangeRef.current = null
    focusEditor()
  }, [closeLinkPopover, focusEditor, restoreSavedRange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Tab') {
        if (!showLinkPopover) {
          clearSelectionState()
        }
      }
    },
    [clearSelectionState, showLinkPopover]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const nextFocusedElement = event.relatedTarget

      if (
        nextFocusedElement &&
        containerRef.current?.contains(nextFocusedElement)
      ) {
        return
      }

      updateEmptyState(editorRef.current)
      onBlur(getNormalizedEditorHtml(editorRef.current))
    },
    [getNormalizedEditorHtml, onBlur, updateEmptyState]
  )

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      updateEmptyState(event.currentTarget)
    },
    [updateEmptyState]
  )

  return (
    <div ref={containerRef} className="description-editor-container">
      <div
        ref={editorRef}
        role="textbox"
        aria-label={placeholder}
        aria-multiline="true"
        className="description-editor"
        contentEditable
        tabIndex={0}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-is-empty={isEditorEmpty}
        onInput={handleInput}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {toolbarState && (
        <SelectionToolbar
          position={toolbarState.position}
          showLinkPanel={showLinkPopover}
          isBold={toolbarState.isBold}
          isItalic={toolbarState.isItalic}
          isUnderline={toolbarState.isUnderline}
          isLink={toolbarState.isLink}
          linkInitialUrl={linkPanelInitialUrl}
          onBold={() => handleFormat('bold')}
          onItalic={() => handleFormat('italic')}
          onUnderline={() => handleFormat('underline')}
          onLinkClick={handleLinkClick}
          onLinkConfirm={handleLinkConfirm}
          onLinkUnlink={handleUnlink}
          onLinkCancel={handleLinkCancel}
        />
      )}
    </div>
  )
}

export { RichTextEditor }
